import { useStore } from '@nanostores/react';
import type { TextSearchMatch } from '@webcontainer/api';
import { useMemo } from 'react';
import { workbenchStore } from '~/lib/stores/workbench';
import { textMeasurer } from '~/utils/text-measurer';

const segmenter = new Intl.Segmenter('en', { granularity: 'grapheme' });
const segmentString = (str: string) => [...segmenter.segment(str)];
const monoFont = getComputedStyle(document.documentElement).getPropertyValue('--bolt-font-monospace');

export function useSearchMatch(
  match: TextSearchMatch,
  options: {
    fontSize?: string;
  },
) {
  const renderedCharCount = useStore(workbenchStore.searchStore.renderedCharCount);

  const regularCharWidth = useMemo(
    () => textMeasurer.measure('a', `${options.fontSize} ${monoFont}`),
    [options.fontSize, monoFont],
  );

  return useMemo(() => {
    const suffix = match.preview.text.slice(match.ranges[0].endColumn);
    const highlight = match.preview.text.slice(match.ranges[0].startColumn, match.ranges[0].endColumn);
    const prefix = match.preview.text.slice(0, match.ranges[0].startColumn).trimStart();

    const segmentedPrefix = segmentString(prefix);
    const availableSpace = renderedCharCount * regularCharWidth;
    const spaceConsumedByHighlight = textMeasurer.measure(highlight, monoFont);

    let spaceLeft = availableSpace - spaceConsumedByHighlight;
    let numberOfSegmentsThatCanFit = 0;

    for (const segment of segmentedPrefix.reverse()) {
      const segmentWidth = textMeasurer.measure(segment.segment, monoFont);

      if (segmentWidth > spaceLeft) {
        break;
      }

      spaceLeft -= segmentWidth;
      numberOfSegmentsThatCanFit++;
    }

    const showInitialEllipsis = numberOfSegmentsThatCanFit < segmentedPrefix.length;

    const prefixString = segmentedPrefix
      .slice(0, numberOfSegmentsThatCanFit)
      .reverse()
      .slice(showInitialEllipsis ? 1 : 0)
      .map((segment) => segment.segment)
      .join('');

    return {
      prefix: prefixString,
      highlight,
      suffix,
      showInitialEllipsis,
    };
  }, [match, renderedCharCount, monoFont, regularCharWidth]);
}
